
import { useState, useEffect, useCallback } from 'react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface Usuario {
  _id: string
  nome: string
  email: string
  telefone?: string
  cpf?: string
  senha?: string
  perfil: 'administrador' | 'juridico' | 'financeiro' | 'compras' | 'engenheiro' | 'gestor'
  status: 'ativo' | 'inativo' | 'bloqueado'
  departamento?: string
  cargo?: string
  dataAdmissao?: string
  permissoes: string[]
  permissoesDetalhadas: {
    [modulo: string]: {
      visualizar: boolean
      adicionar: boolean
      editar: boolean
      excluir: boolean
    }
  }
  observacoes?: string
  ultimoLogin?: string
  creator: string
  createdAt: string
  updatedAt: string
}

interface UsuarioMetrics {
  totalUsuarios: number
  usuariosAtivos: number
  usuariosInativos: number
  usuariosBloqueados: number
  distribuicaoPorPerfil: {
    administrador: number
    juridico: number
    financeiro: number
    compras: number
    engenheiro: number
    gestor: number
  }
  loginsRecentes: number
}

export const useUsuarios = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState<UsuarioMetrics>({
    totalUsuarios: 0,
    usuariosAtivos: 0,
    usuariosInativos: 0,
    usuariosBloqueados: 0,
    distribuicaoPorPerfil: {
      administrador: 0,
      juridico: 0,
      financeiro: 0,
      compras: 0,
      engenheiro: 0,
      gestor: 0
    },
    loginsRecentes: 0
  })

  // Fetch all users
  const fetchUsuarios = useCallback(async () => {
    try {
      setLoading(true)
      const { list } = await lumi.entities.usuarios.list()
      setUsuarios(list || [])
      calculateMetrics(list || [])
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
      toast.error('Erro ao carregar usuários')
    } finally {
      setLoading(false)
    }
  }, [])

  // Calculate metrics
  const calculateMetrics = (usuariosData: Usuario[]) => {
    const totalUsuarios = usuariosData.length
    const usuariosAtivos = usuariosData.filter(u => u.status === 'ativo').length
    const usuariosInativos = usuariosData.filter(u => u.status === 'inativo').length
    const usuariosBloqueados = usuariosData.filter(u => u.status === 'bloqueado').length

    // Distribuição por perfil
    const distribuicaoPorPerfil = {
      administrador: usuariosData.filter(u => u.perfil === 'administrador').length,
      juridico: usuariosData.filter(u => u.perfil === 'juridico').length,
      financeiro: usuariosData.filter(u => u.perfil === 'financeiro').length,
      compras: usuariosData.filter(u => u.perfil === 'compras').length,
      engenheiro: usuariosData.filter(u => u.perfil === 'engenheiro').length,
      gestor: usuariosData.filter(u => u.perfil === 'gestor').length
    }

    // Logins recentes (últimos 7 dias)
    const seteDiasAtras = new Date()
    seteDiasAtras.setDate(seteDiasAtras.getDate() - 7)
    
    const loginsRecentes = usuariosData.filter(u => {
      if (!u.ultimoLogin) return false
      const dataLogin = new Date(u.ultimoLogin)
      return dataLogin >= seteDiasAtras
    }).length

    setMetrics({
      totalUsuarios,
      usuariosAtivos,
      usuariosInativos,
      usuariosBloqueados,
      distribuicaoPorPerfil,
      loginsRecentes
    })
  }

  // Create user
  const createUsuario = async (usuarioData: Omit<Usuario, '_id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // Verificar se email já existe
      const existingUser = usuarios.find(u => u.email === usuarioData.email)
      if (existingUser) {
        toast.error('Email já cadastrado no sistema')
        return null
      }

      // Validar senha
      if (!usuarioData.senha || usuarioData.senha.length < 6) {
        toast.error('Senha deve ter pelo menos 6 caracteres')
        return null
      }

      // Definir permissões padrão baseadas no perfil
      const permissoesPadrao = getPermissoesPorPerfil(usuarioData.perfil)
      const permissoesDetalhadasPadrao = getPermissoesDetalhadasPorPerfil(usuarioData.perfil)

      const newUsuario = await lumi.entities.usuarios.create({
        ...usuarioData,
        permissoes: usuarioData.permissoes.length > 0 ? usuarioData.permissoes : permissoesPadrao,
        permissoesDetalhadas: usuarioData.permissoesDetalhadas || permissoesDetalhadasPadrao,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      await fetchUsuarios()
      toast.success('Usuário criado com sucesso!')
      return newUsuario
    } catch (error) {
      console.error('Erro ao criar usuário:', error)
      toast.error('Erro ao criar usuário')
      throw error
    }
  }

  // Update user
  const updateUsuario = async (id: string, usuarioData: Partial<Usuario>) => {
    try {
      // Verificar se email já existe em outro usuário
      if (usuarioData.email) {
        const existingUser = usuarios.find(u => u.email === usuarioData.email && u._id !== id)
        if (existingUser) {
          toast.error('Email já cadastrado para outro usuário')
          return null
        }
      }

      // Validar senha se fornecida
      if (usuarioData.senha && usuarioData.senha.length < 6) {
        toast.error('Senha deve ter pelo menos 6 caracteres')
        return null
      }

      // Se senha estiver vazia, não incluir no update
      const updateData = { ...usuarioData }
      if (!updateData.senha || updateData.senha.trim() === '') {
        delete updateData.senha
      }

      const updatedUsuario = await lumi.entities.usuarios.update(id, {
        ...updateData,
        updatedAt: new Date().toISOString()
      })

      await fetchUsuarios()
      toast.success('Usuário atualizado com sucesso!')
      return updatedUsuario
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error)
      toast.error('Erro ao atualizar usuário')
      throw error
    }
  }

  // Delete user
  const deleteUsuario = async (id: string) => {
    try {
      await lumi.entities.usuarios.delete(id)
      await fetchUsuarios()
      toast.success('Usuário excluído com sucesso!')
    } catch (error) {
      console.error('Erro ao excluir usuário:', error)
      toast.error('Erro ao excluir usuário')
      throw error
    }
  }

  // Update last login
  const updateUltimoLogin = async (id: string) => {
    try {
      await lumi.entities.usuarios.update(id, {
        ultimoLogin: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      await fetchUsuarios()
    } catch (error) {
      console.error('Erro ao atualizar último login:', error)
    }
  }

  // Get permissions by profile
  const getPermissoesPorPerfil = (perfil: Usuario['perfil']): string[] => {
    const permissoes: Record<Usuario['perfil'], string[]> = {
      administrador: [
        'criar_usuario', 'editar_usuario', 'excluir_usuario', 'gerenciar_sistema',
        'relatorios_completos', 'dashboard_completo', 'todas_permissoes'
      ],
      juridico: [
        'gerenciar_contratos', 'aprovar_contratos', 'templates_juridicos',
        'relatorios_juridicos', 'dashboard_juridico'
      ],
      financeiro: [
        'gerenciar_financeiro', 'movimentacao_bancaria', 'relatorios_financeiros',
        'contas_pagar_receber', 'dashboard_financeiro'
      ],
      compras: [
        'gerenciar_compras', 'aprovar_compras', 'cotacoes', 'fornecedores',
        'dashboard_compras'
      ],
      engenheiro: [
        'planejamento_obras', 'orcamentos', 'diario_obra', 'insumos',
        'atividades_obra', 'dashboard_engenharia'
      ],
      gestor: [
        'gerenciar_projetos', 'dashboard_completo', 'relatorios_gerenciais',
        'funcionarios', 'dashboard_gestao'
      ]
    }

    return permissoes[perfil] || []
  }

  // Get detailed permissions by profile
  const getPermissoesDetalhadasPorPerfil = (perfil: Usuario['perfil']) => {
    const modulosDisponiveis = [
      'crm', 'base_insumos', 'orcamentos', 'contratos', 'funcionarios', 
      'planejamento_obras', 'compras', 'financeiro', 'usuarios', 'dashboard'
    ]

    const permissoesCompletas = {
      visualizar: true,
      adicionar: true,
      editar: true,
      excluir: true
    }

    const permissoesLimitadas = {
      visualizar: true,
      adicionar: false,
      editar: false,
      excluir: false
    }

    const permissoesPorPerfil: Record<Usuario['perfil'], any> = {
      administrador: modulosDisponiveis.reduce((acc, modulo) => ({
        ...acc,
        [modulo]: permissoesCompletas
      }), {}),
      
      juridico: {
        contratos: permissoesCompletas,
        dashboard: { visualizar: true, adicionar: false, editar: false, excluir: false },
        ...modulosDisponiveis.filter(m => !['contratos', 'dashboard'].includes(m))
          .reduce((acc, modulo) => ({ ...acc, [modulo]: permissoesLimitadas }), {})
      },
      
      financeiro: {
        financeiro: permissoesCompletas,
        compras: { visualizar: true, adicionar: false, editar: true, excluir: false },
        dashboard: { visualizar: true, adicionar: false, editar: false, excluir: false },
        ...modulosDisponiveis.filter(m => !['financeiro', 'compras', 'dashboard'].includes(m))
          .reduce((acc, modulo) => ({ ...acc, [modulo]: permissoesLimitadas }), {})
      },
      
      compras: {
        compras: permissoesCompletas,
        base_insumos: { visualizar: true, adicionar: true, editar: true, excluir: false },
        orcamentos: { visualizar: true, adicionar: false, editar: false, excluir: false },
        dashboard: { visualizar: true, adicionar: false, editar: false, excluir: false },
        ...modulosDisponiveis.filter(m => !['compras', 'base_insumos', 'orcamentos', 'dashboard'].includes(m))
          .reduce((acc, modulo) => ({ ...acc, [modulo]: permissoesLimitadas }), {})
      },
      
      engenheiro: {
        planejamento_obras: permissoesCompletas,
        orcamentos: permissoesCompletas,
        base_insumos: { visualizar: true, adicionar: true, editar: true, excluir: false },
        compras: { visualizar: true, adicionar: true, editar: false, excluir: false },
        dashboard: { visualizar: true, adicionar: false, editar: false, excluir: false },
        ...modulosDisponiveis.filter(m => !['planejamento_obras', 'orcamentos', 'base_insumos', 'compras', 'dashboard'].includes(m))
          .reduce((acc, modulo) => ({ ...acc, [modulo]: permissoesLimitadas }), {})
      },
      
      gestor: {
        funcionarios: permissoesCompletas,
        dashboard: permissoesCompletas,
        ...modulosDisponiveis.filter(m => !['funcionarios', 'dashboard'].includes(m))
          .reduce((acc, modulo) => ({ ...acc, [modulo]: { visualizar: true, adicionar: false, editar: true, excluir: false } }), {})
      }
    }

    return permissoesPorPerfil[perfil] || {}
  }

  // Get available modules
  const getModulosDisponiveis = () => {
    return [
      { key: 'crm', label: 'CRM - Leads' },
      { key: 'base_insumos', label: 'Base de Insumos' },
      { key: 'orcamentos', label: 'Orçamentos' },
      { key: 'contratos', label: 'Contratos' },
      { key: 'funcionarios', label: 'Funcionários' },
      { key: 'planejamento_obras', label: 'Planejamento de Obras' },
      { key: 'compras', label: 'Compras' },
      { key: 'financeiro', label: 'Financeiro' },
      { key: 'usuarios', label: 'Gestão de Usuários' },
      { key: 'dashboard', label: 'Dashboard' }
    ]
  }

  // Get users by profile
  const getUsuariosPorPerfil = (perfil: Usuario['perfil']) => {
    return usuarios.filter(usuario => usuario.perfil === perfil && usuario.status === 'ativo')
  }

  // Get active users
  const getUsuariosAtivos = () => {
    return usuarios.filter(usuario => usuario.status === 'ativo')
  }

  useEffect(() => {
    fetchUsuarios()
  }, [fetchUsuarios])

  return {
    // Data
    usuarios,
    loading,
    metrics,
    
    // CRUD Operations
    createUsuario,
    updateUsuario,
    deleteUsuario,
    updateUltimoLogin,
    
    // Utility functions
    fetchUsuarios,
    getPermissoesPorPerfil,
    getPermissoesDetalhadasPorPerfil,
    getModulosDisponiveis,
    getUsuariosPorPerfil,
    getUsuariosAtivos
  }
}
